import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBolt } from '@fortawesome/free-solid-svg-icons';
import { STRIPE_CONFIG, LIFETIME_DEAL_LIMIT } from '../config/stripe';
import { getWebAppUrl } from '../utils/config';
import { DEFAULT_TRIAL_PERIOD_DAYS } from '../config/config';
import NewsletterSignupForm from './NewsletterSignupForm';

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  lifetimePrice: number;
  description: string;
  features: string[];
  highlighted?: boolean;
  priceIds: {
    monthly: string;
    yearly: string;
    lifetime?: string;
  };
}

const pricingPlans: PricingPlan[] = [
  {
    name: 'Starter',
    monthlyPrice: 49,
    yearlyPrice: 470,
    lifetimePrice: 599,
    description: 'Perfect for solo cleaners starting out',
    features: [
      'Up to 2 team members',
      'Customer management (CRM)',
      'Appointment scheduling',
      'Basic invoicing',
      'Email reminders',
      'Mobile app access',
      'Basic reporting'
    ],
    priceIds: STRIPE_CONFIG.priceIds.starter
  },
  {
    name: 'Professional',
    monthlyPrice: 79,
    yearlyPrice: 790,
    lifetimePrice: 149,
    description: 'For growing cleaning businesses',
    features: [
      'Up to 5 team members',
      '**Everything in Starter**',
      'Online payment processing',
      'Employee management',
      'Time tracking & timesheets',
      'Inventory management',
      'Service item catalog',
      'SMS notifications',
      'Document storage (10GB)',
      'Priority support'
    ],
    highlighted: true,
    priceIds: STRIPE_CONFIG.priceIds.professional
  },
  {
    name: 'Enterprise',
    monthlyPrice: 149,
    yearlyPrice: 1490,
    lifetimePrice: 299,
    description: 'For large operations and franchises',
    features: [
      'Unlimited team members',
      '**Everything in Professional**',
      'Analytics & Reports dashboard',
      'Order management',
      'Calendar sync (Google)',
      'Unlimited document storage',
      'Dedicated account manager',
      'Custom training'
    ],
    priceIds: STRIPE_CONFIG.priceIds.enterprise
  }
];

const definedPlan: PricingPlan = {
  name: 'Defined',
  monthlyPrice: 350,
  yearlyPrice: 3360,
  lifetimePrice: 499,
  description: 'For businesses needing long-term project management with employee time-tracking for up to 20 employees included',
  features: [
    '**Everything in Enterprise**',
    'Up to 20 employees with time-tracking included',
    'No Workforce Max Add-on needed',
    'Long-term project management',
    'Task management & tracking',
    'Resource scheduling',
    'Team collaboration tools',
    'Progress monitoring',
    'Integrated project billing',
    'Time tracking per project',
    'Client project portals'
  ],
  priceIds: STRIPE_CONFIG.priceIds.defined
};

const Pricing: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);

  const handleSubscribe = (plan: PricingPlan) => {
    const webAppUrl = getWebAppUrl();
    const billing = isYearly ? 'yearly' : 'monthly';
    window.location.href = `${webAppUrl}/create-account?plan=${plan.name.toLowerCase()}&billing=${billing}`;
  };

  return (
    <section id="pricing" className="pricing-section-modern">
      <Container>
        <Row className="mb-5">
          <Col lg={8} className="mx-auto text-center">
            <h2 className="section-title mb-3">
              Simple pricing
            </h2>
            <p className="section-subtitle mb-4">
              Choose the plan that fits your business
            </p>

            <div className="pricing-toggle-modern">
              <button
                className={`toggle-button-modern ${!isYearly ? 'active' : ''}`}
                onClick={() => setIsYearly(false)}
              >
                Monthly
              </button>
              <button
                className={`toggle-button-modern ${isYearly ? 'active' : ''}`}
                onClick={() => setIsYearly(true)}
              >
                Yearly
                <span className="save-badge-modern">Save 20%</span>
              </button>
            </div>
          </Col>
        </Row>

        <Row>
          {pricingPlans.map((plan, index) => (
            <Col key={index} lg={4} md={6} className="mb-4">
              <Card
                className={`h-100 shadow ${plan.highlighted ? 'border-primary border-2' : 'border-0'}`}
              >
                {plan.highlighted && (
                  <div className="bg-primary text-white text-center py-2">
                    <small className="fw-bold">MOST POPULAR</small>
                  </div>
                )}
                <Card.Body className="p-4">
                  <h3 className="mb-2">{plan.name}</h3>
                  <p className="text-muted mb-4">{plan.description}</p>

                  <div className="mb-4">
                    <span className="display-4 fw-bold">
                      ${isYearly ? Math.round(plan.yearlyPrice / 12) : plan.monthlyPrice}
                    </span>
                    <span className="text-muted">/month</span>
                    {isYearly && (
                      <div className="text-success small">
                        Billed ${plan.yearlyPrice} yearly
                      </div>
                    )}
                  </div>

                  <Button
                    variant={plan.highlighted ? 'primary' : 'outline-primary'}
                    className={`w-100 mb-4 rounded-pill ${plan.highlighted ? 'btn-primary-custom' : ''}`}
                    onClick={() => handleSubscribe(plan)}
                  >
                    {DEFAULT_TRIAL_PERIOD_DAYS > 0 ? `Start ${DEFAULT_TRIAL_PERIOD_DAYS}-Day Free Trial` : 'Get Started'}
                  </Button>

                  {/* Lifetime Deal Section - Above Features */}
                  {plan.priceIds.lifetime && (
                    <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <FontAwesomeIcon icon={faBolt} className="me-2" style={{ color: '#dc2626' }} />
                        <span className="fw-bold" style={{ color: '#dc2626' }}>Lifetime Deal</span>
                        <Badge className="ms-2" style={{ backgroundColor: '#dc2626', fontSize: '0.7rem' }}>
                          Only {LIFETIME_DEAL_LIMIT} left
                        </Badge>
                      </div>
                      <div className="text-center">
                        <span className="h3 fw-bold" style={{ color: '#dc2626' }}>${plan.lifetimePrice}</span>
                      </div>

                      <div className="text-center text-success small fw-bold">
                        Save ${(plan.monthlyPrice * 12 * 5) - plan.lifetimePrice}!
                      </div>
                      <Button
                        variant="danger"
                        className="w-100 mt-2 rounded-pill fw-bold"
                        onClick={() => {
                          const webAppUrl = getWebAppUrl();
                          window.location.href = `${webAppUrl}/create-account?plan=${plan.name.toLowerCase()}&billing=lifetime`;
                        }}
                      >
                        <FontAwesomeIcon icon={faBolt} className="me-2" />
                        Get Lifetime Deal
                      </Button>
                    </div>
                  )}

                  <h5 className="mb-3">Features:</h5>
                  <ul className="list-unstyled">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="mb-2 d-flex align-items-start">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-success me-2 mt-1"
                        />
                        <span>
                          {feature.includes('**') ? (
                            <strong>{feature.replace(/\*\*/g, '')}</strong>
                          ) : (
                            feature
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Defined Plan - Separate Row */}
        <Row className="mt-5 mb-4">
          <Col lg={8} className="mx-auto">
            <Card className="border-0 shadow-lg" style={{ backgroundColor: '#fff8f0', border: '2px solid #ff871e' }}>
              <div className="text-white text-center py-2" style={{ backgroundColor: '#ff871e' }}>
                <small className="fw-bold">COMPLETE SOLUTION</small>
              </div>
              <Card.Body className="p-5">
                <Row className="align-items-center">
                  <Col lg={7}>
                    <h3 className="mb-2" style={{ color: '#ff871e' }}>{definedPlan.name}</h3>
                    <p className="text-muted mb-4">{definedPlan.description}</p>
                    <Row>
                      <Col md={6}>
                        <ul className="list-unstyled">
                          {definedPlan.features.slice(0, 6).map((feature, featureIndex) => (
                            <li key={featureIndex} className="mb-2 d-flex align-items-start">
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="me-2 mt-1"
                                style={{ color: '#ff871e' }}
                              />
                              <span>
                                {feature.includes('**') ? (
                                  <strong>{feature.replace(/\*\*/g, '')}</strong>
                                ) : (
                                  feature
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Col>
                      <Col md={6}>
                        <ul className="list-unstyled">
                          {definedPlan.features.slice(6).map((feature, featureIndex) => (
                            <li key={featureIndex} className="mb-2 d-flex align-items-start">
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="me-2 mt-1"
                                style={{ color: '#ff871e' }}
                              />
                              <span>
                                {feature.includes('**') ? (
                                  <strong>{feature.replace(/\*\*/g, '')}</strong>
                                ) : (
                                  feature
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={5} className="text-center">
                    <div className="p-4">
                      <div className="mb-3">
                        <span className="display-4 fw-bold" style={{ color: '#ff871e' }}>
                          ${isYearly ? Math.round(definedPlan.yearlyPrice / 12) : definedPlan.monthlyPrice}
                        </span>
                        <span className="text-muted">/month</span>
                        {isYearly && (
                          <div className="text-success small">
                            Billed ${definedPlan.yearlyPrice} yearly
                          </div>
                        )}
                      </div>
                      <Button
                        className="w-100 rounded-pill fw-bold"
                        style={{ backgroundColor: '#ff871e', border: 'none' }}
                        onClick={() => handleSubscribe(definedPlan)}
                      >
                        {DEFAULT_TRIAL_PERIOD_DAYS > 0 ? `Start ${DEFAULT_TRIAL_PERIOD_DAYS}-Day Free Trial` : 'Get Started'}
                      </Button>
                      <p className="small text-muted mt-2 mb-0">
                        Includes 20 employees with time-tracking
                      </p>

                      {/* Lifetime Deal for Defined */}
                      {definedPlan.priceIds.lifetime && (
                        <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <FontAwesomeIcon icon={faBolt} className="me-2" style={{ color: '#dc2626' }} />
                            <span className="fw-bold" style={{ color: '#dc2626' }}>Lifetime Deal</span>
                            <Badge className="ms-2" style={{ backgroundColor: '#dc2626', fontSize: '0.7rem' }}>
                              Only {LIFETIME_DEAL_LIMIT} left
                            </Badge>
                          </div>
                          <div className="text-center">
                            <span className="h3 fw-bold" style={{ color: '#dc2626' }}>${definedPlan.lifetimePrice}</span>
                            <span className="text-muted text-decoration-line-through ms-2">${definedPlan.monthlyPrice * 12 * 5} over 5 yrs</span>
                          </div>
                          <div className="text-center text-success small fw-bold">
                            Save ${(definedPlan.monthlyPrice * 12 * 5) - definedPlan.lifetimePrice}!
                          </div>
                          <div className="text-center small fw-bold mt-1" style={{ color: '#dc2626' }}>
                            Price increases February 7th!
                          </div>
                          <Button
                            variant="danger"
                            className="w-100 mt-2 rounded-pill fw-bold"
                            onClick={() => {
                              const webAppUrl = getWebAppUrl();
                              window.location.href = `${webAppUrl}/create-account?plan=${definedPlan.name.toLowerCase()}&billing=lifetime`;
                            }}
                          >
                            <FontAwesomeIcon icon={faBolt} className="me-2" />
                            Get Lifetime Deal
                          </Button>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="text-center">
            {DEFAULT_TRIAL_PERIOD_DAYS > 0 && (
              <p className="text-muted">
                All plans include a {DEFAULT_TRIAL_PERIOD_DAYS}-day free trial. No credit card required.
              </p>
            )}
            <p className="text-muted">
              Need custom integrations or a revenue pipeline built for your team? <a href="mailto:support@thedefinedcrm.com" style={{ color: '#074a5a' }}>Contact our sales team</a>
            </p>
          </Col>
        </Row>

        {/* Custom Integrations & Pipelines Section */}
        <Row className="mt-5">
          <Col lg={10} className="mx-auto">
            <Card className="border-0 shadow-lg" style={{ backgroundColor: '#eef8fb', border: '2px solid #074a5a' }}>
              <Card.Body className="p-5">
                <Row className="align-items-center">
                  <Col lg={8}>
                    <Badge className="mb-3" style={{ backgroundColor: '#074a5a' }}>CUSTOM BUILD</Badge>
                    <h3 className="mb-3" style={{ color: '#074a5a' }}>
                      Custom Integrations & Revenue Pipelines
                    </h3>
                    <p className="lead text-muted mb-4">
                      We build the exact lead funnel and sales automation workflow your business needs to convert more leads and increase monthly revenue.
                    </p>
                    <Row>
                      <Col md={6}>
                        <h5 className="mb-3" style={{ color: '#074a5a' }}>Scope Examples</h5>
                        <ul className="list-unstyled">
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Lead capture + instant SMS/email response</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>1-3-7-30 day sales follow-up sequences</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Quote, invoice, and payment pipeline automation</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Reactivation funnels for old leads and past clients</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>CRM, forms, ads, calendar, and communication tool integrations</span>
                          </li>
                        </ul>
                      </Col>
                      <Col md={6}>
                        <h5 className="mb-3" style={{ color: '#074a5a' }}>Typical Timeline</h5>
                        <ul className="list-unstyled text-muted">
                          <li className="mb-2"><strong>Week 1:</strong> Revenue audit + funnel mapping</li>
                          <li className="mb-2"><strong>Week 2:</strong> Integration architecture + success metrics</li>
                          <li className="mb-2"><strong>Week 3-4:</strong> Automation build + internal QA</li>
                          <li className="mb-2"><strong>Week 5:</strong> Team rollout + go-live support</li>
                          <li className="mb-2"><strong>Ongoing:</strong> Pipeline optimization based on conversion data</li>
                        </ul>
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={4} className="text-center">
                    <div className="p-4">
                      <h4 className="mb-3" style={{ color: '#074a5a' }}>Implementation Starts At</h4>
                      <div className="mb-2">
                        <span className="display-6 fw-bold" style={{ color: '#074a5a' }}>$1,500</span>
                      </div>
                      <p className="text-muted mb-4">One-time setup + optional monthly optimization retainer</p>
                      <Button
                        className="w-100 rounded-pill fw-bold"
                        style={{ backgroundColor: '#074a5a', border: 'none' }}
                        onClick={() => {
                          window.location.href = 'mailto:support@thedefinedcrm.com?subject=Custom%20Integrations%20%26%20Revenue%20Pipelines';
                        }}
                      >
                        Book Discovery Call
                      </Button>
                      <p className="small text-muted mt-3 mb-0">
                        Best for teams serious about predictable growth
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Marketing Add-on Section */}
        <Row className="mt-5 mb-5">
          <Col lg={10} className="mx-auto">
            <Card className="border-0 shadow-lg" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Body className="p-5">
                <Row className="align-items-center">
                  <Col lg={8}>
                    <Badge className="mb-3" style={{ backgroundColor: '#ff871e' }}>ADD-ON</Badge>
                    <h3 className="mb-3" style={{ color: '#074a5a' }}>
                      Marketing Suite Add-on
                    </h3>
                    <p className="lead text-muted mb-4">
                      Supercharge any plan with our comprehensive marketing tools and completely custom marketing solutions built for your business.
                    </p>
                    <Row>
                      <Col md={6}>
                        <ul className="list-unstyled">
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Marketing campaigns</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Email & SMS marketing</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Lead management</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Customer segmentation</span>
                          </li>
                        </ul>
                      </Col>
                      <Col md={6}>
                        <ul className="list-unstyled">
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Marketing templates</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Campaign analytics</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Automated follow-ups</span>
                          </li>
                          <li className="mb-2 d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1" />
                            <span>Completely custom marketing solutions</span>
                          </li>
                        </ul>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <h5 className="mb-2" style={{ color: '#074a5a' }}>How custom solutions work</h5>
                      <ul className="list-unstyled text-muted mb-0">
                        <li>Discovery: We map your current process and bottlenecks</li>
                        <li>Build plan: We define the exact automations and integrations</li>
                        <li>Implementation: We build, test, and deploy with your team</li>
                        <li>Optimization: We monitor results and refine over time</li>
                      </ul>
                    </div>
                  </Col>
                  <Col lg={4} className="text-center">
                    <div className="p-4">
                      <div className="mb-3">
                        <span className="display-4 fw-bold" style={{ color: '#074a5a' }}>
                          ${isYearly ? Math.round(1920 / 12) : 200}
                        </span>
                        <span className="text-muted">/month</span>
                        {isYearly && (
                          <div className="text-success small">
                            Billed $1920 yearly
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline-primary"
                        className="w-100 rounded-pill"
                        onClick={() => {
                          const webAppUrl = getWebAppUrl();
                          const billing = isYearly ? 'yearly' : 'monthly';
                          window.location.href = `${webAppUrl}/create-account?plan=marketing&billing=${billing}`;
                        }}
                      >
                        Add to Any Plan
                      </Button>
                      <p className="small text-muted mt-2 mb-0">
                        Compatible with all plans
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Newsletter Signup Section */}
        <Row className="mt-5">
          <Col lg={8} className="mx-auto">
            <div className="p-4 bg-primary bg-opacity-10 rounded-3 text-center">
              <h4 className="mb-3" style={{ color: '#074a5a' }}>Get Free Weekly Business Tips</h4>
              <p className="text-muted mb-4">
                Join thousands of cleaning business owners who receive expert advice every week
              </p>
              <NewsletterSignupForm
                onSuccess={() => {
                  console.log('Successfully subscribed from pricing section!');
                }}
                source="inline"
                compact={true}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Pricing;
